import classnames from 'classnames';
import React, { FunctionComponent, HTMLAttributes } from 'react';

import { Meta } from '@api/v4/ApiResponseTypes';
import { TextButton } from '@components/library/button';
import { ListDropdown, ListOption } from '@components/library/dropdown';

import classes from '../styles/pagination.module.scss';

interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  nextPage: number | null;
  onPageChange: (page: number) => void;
  prevPage: number | null;
  totalCount: number;
  totalPages: number;
  hideWhenAllShown?: boolean;
  loading?: boolean;
  per?: number;
  perOptions?: ListOption[];
  onPerChange?: (perPage: PerPageEnum) => void;
}

// kaminari is one based
const defaultFirstPage = 1;

export enum PerPageEnum {
  TwentyFive = 25,
  Fifty = 50,
  OneHundred = 100
}

export const defaultPerOptions: ListOption[] = [
  {
    label: PerPageEnum.TwentyFive,
    value: PerPageEnum.TwentyFive
  },
  {
    label: PerPageEnum.Fifty,
    value: PerPageEnum.Fifty
  },
  {
    label: PerPageEnum.OneHundred,
    value: PerPageEnum.OneHundred
  }
];

export const Pagination: FunctionComponent<PaginationProps> = (props) => {
  const {
    currentPage,
    hideWhenAllShown = false,
    loading,
    nextPage,
    onPageChange,
    onPerChange,
    per = PerPageEnum.TwentyFive,
    perOptions = defaultPerOptions,
    prevPage,
    totalCount,
    totalPages,
    ...otherProps
  } = props;

  const allShown = totalCount <= per;
  if (hideWhenAllShown && allShown) return null;

  return (
    <div {...otherProps} className={classnames(classes.pagination, otherProps.className)}>
      <div className={classes.buttonGroup}>
        <TextButton
          disabled={loading || currentPage === defaultFirstPage}
          onClick={(): void => onPageChange(defaultFirstPage)}
          type="button"
        >
          {bfTranslate('First')}
        </TextButton>
        <TextButton
          disabled={loading || !prevPage}
          onClick={(): void => {
            if (prevPage) {
              onPageChange(prevPage);
            }
          }}
          type="button"
        >
          {bfTranslate('Prev')}
        </TextButton>
        <TextButton
          disabled={loading || !nextPage}
          onClick={(): void => {
            if (nextPage) {
              onPageChange(nextPage);
            }
          }}
          type="button"
        >
          {bfTranslate('Next')}
        </TextButton>
        <TextButton
          disabled={loading || currentPage === totalPages}
          onClick={(): void => onPageChange(totalPages)}
          type="button"
        >
          {bfTranslate('Last')}
        </TextButton>
      </div>
      <ListDropdown
        className={classes.perPage}
        onChange={(listOption: ListOption): void => {
          if (onPerChange) {
            onPerChange(listOption.value as PerPageEnum);
          }
        }}
        openOnClick
        openOnHover={false}
        options={perOptions}
        placeholder={per.toString()}
        value={per.toString()}
      />
      <p className={classes.totalCount}>
        {bfTranslate('Page')} {currentPage}/{totalPages} <span className={classes.bullet}>&bull;</span> {bfTranslate('Total items:')} {totalCount}
      </p>
    </div>
  );
};

interface Pagination {
  currentPage: number;
  nextPage: number | null;
  per: number;
  prevPage: number | null;
  totalCount: number;
  totalPages: number;
}

export const defaultPagination: Pagination = {
  currentPage: 1,
  nextPage: null,
  per: defaultPerOptions[0].value as PerPageEnum,
  prevPage: null,
  totalCount: 0,
  totalPages: 0
};

/**
 * Map JSONAPI meta pagination attributes to JS pagination
 * https://jsonapi.org/format/#document-meta
 * @param {Meta} meta Meta
 * @param {PerPageEnum} per PerPageEnum
 * @returns {Pagination} Pagination
 */
export const mapMetaToPagination = (meta?: Meta, per?: PerPageEnum): Pagination => ({
  currentPage: meta?.current_page || defaultPagination.currentPage,
  nextPage: meta?.next_page || defaultPagination.nextPage,
  per: per || defaultPerOptions[0].value as PerPageEnum,
  prevPage: meta?.prev_page || defaultPagination.prevPage,
  totalCount: meta?.total_count || defaultPagination.totalCount,
  totalPages: meta?.total_pages || defaultPagination.totalPages
});
